import { mkdir, readFile, rename, rm, writeFile } from 'node:fs/promises';
import path from 'node:path';

export const MAX_STORED_SEARCHES = 5_000;

export interface StoredSearch {
  checkedAt: string;
  url: string;
  classification: string;
}

export interface SearchFile {
  total: number;
  searches: StoredSearch[];
}

export interface SearchRecorder {
  record(entry: StoredSearch): Promise<void>;
  total(): Promise<number>;
}

export function disabledSearchLog(): SearchRecorder {
  return {
    async record() {},
    async total() {
      return 0;
    },
  };
}

export function createSearchLog(filePath: string | null): SearchRecorder {
  if (!filePath) return disabledSearchLog();
  return new FileSearchLog(filePath);
}

class FileSearchLog implements SearchRecorder {
  private queue: Promise<void> = Promise.resolve();
  private cache: SearchFile | null = null;

  constructor(private readonly filePath: string) {}

  record(entry: StoredSearch): Promise<void> {
    const run = this.queue.then(() => this.writeEntry(entry));
    this.queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  async total(): Promise<number> {
    const run = this.queue.then(() => this.load());
    this.queue = run.then(
      () => undefined,
      () => undefined,
    );
    const data = await run;
    return data.total;
  }

  private async writeEntry(entry: StoredSearch): Promise<void> {
    const current = await this.load();
    const next: SearchFile = {
      total: current.total + 1,
      searches: [entry, ...current.searches].slice(0, MAX_STORED_SEARCHES),
    };
    await writeSearchFile(this.filePath, next);
    this.cache = next;
  }

  private async load(): Promise<SearchFile> {
    if (this.cache) return this.cache;
    try {
      const raw = await readFile(this.filePath, 'utf8');
      this.cache = parseSearchFile(raw);
    } catch (error) {
      if (isNotFound(error)) this.cache = { total: 0, searches: [] };
      else throw error;
    }
    return this.cache;
  }
}

function isNotFound(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'code' in error && (error as { code?: string }).code === 'ENOENT';
}

export function parseSearchFile(raw: string): SearchFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('invalid search log');
  }
  if (!parsed || typeof parsed !== 'object') throw new Error('invalid search log');
  const record = parsed as { total?: unknown; searches?: unknown };
  if (typeof record.total !== 'number' || !Number.isInteger(record.total) || record.total < 0) {
    throw new Error('invalid search log');
  }
  if (!Array.isArray(record.searches)) throw new Error('invalid search log');

  const searches: StoredSearch[] = [];
  for (const item of record.searches) {
    if (!item || typeof item !== 'object') continue;
    const row = item as { checkedAt?: unknown; url?: unknown; classification?: unknown };
    if (typeof row.checkedAt !== 'string' || typeof row.url !== 'string' || typeof row.classification !== 'string') continue;
    if (!row.checkedAt || !row.url || !row.classification) continue;
    searches.push({
      checkedAt: row.checkedAt.slice(0, 40),
      url: row.url.slice(0, 2048),
      classification: row.classification.slice(0, 80),
    });
  }
  return { total: record.total, searches: searches.slice(0, MAX_STORED_SEARCHES) };
}

async function writeSearchFile(filePath: string, data: SearchFile): Promise<void> {
  await mkdir(path.dirname(filePath), { recursive: true });
  const body = `${JSON.stringify(data, null, 2)}\n`;
  const temp = `${filePath}.${process.pid}.tmp`;
  await writeFile(temp, body, 'utf8');
  try {
    await rename(temp, filePath);
  } catch {
    await writeFile(filePath, body, 'utf8');
    await rm(temp, { force: true });
  }
}
